
import { defineComponent, PropType, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { FieldsetStructure, FieldsStructure, FieldStructure, InputValueUpdatedStructure, FormDataStructure, ValidationErrorsStructure } from '@/contracts/forms/FieldContracts'
import { ButtonsStructure } from '@/contracts/forms/ButtonContracts'
import { hasErrors, validateInput } from '@/composables/validation'
import Input from '@/components/forms/inputs/Input.vue'
import Select from '@/components/forms/inputs/Select.vue'
import Date from '@/components/forms/inputs/Date.vue'
import Radio from '@/components/forms/inputs/Radio.vue'
import Submit from '@/components/forms/buttons/Submit.vue'

export default defineComponent(
{
    name: 'Form',
    emits: ['formHasPassedValidation', 'formFieldValuesUpdated'],
    components:
    {
        Input,
        Date,
        Radio,
        Select,
        Submit
    },
    props:
    {
        fieldsets:
        {
            type: Array as PropType<Array<FieldsetStructure>>,
            default: new Array<FieldsetStructure>()
        },
        fields:
        {
            type: Object as PropType<FieldsStructure>,
            default: {} as FieldsStructure
        },
        buttons:
        {
            type: Object as PropType<ButtonsStructure>,
            default: {} as ButtonsStructure
        }
    },
    setup(props, { emit })
    {
        // Load router:
        const router = useRouter()
        // Load vuex:
        const store = useStore()
        // Hold form data:
        const formFieldsets = reactive(props.fieldsets as Array<FieldsetStructure>)
        const formFields = reactive(props.fields as FieldsStructure)
        const formButtons = reactive(props.buttons as ButtonsStructure)
        const formData = reactive({} as FormDataStructure)
        // Set fields visibility:
        for(const [name, field] of Object.entries(formFields))
        {
            field.visible = computed(() =>
            {
                if(field.show !== undefined)
                {
                    return field.show(formFields)
                }
                return true
            })
        }
        // Get fields inside fieldset:
        formFieldsets.forEach((fieldset) =>
        {
            // Set fields inside fieldset:
            fieldset.fields = computed(() =>
            {
                const fieldsInFieldset = {} as FieldsStructure
                for(const [name, field] of Object.entries(formFields))
                {
                    if(field.fieldset === fieldset.name)
                    {
                        fieldsInFieldset[name] = field
                    }
                }
                return fieldsInFieldset
            })
            // Set fieldset visibility based on child fields visibility:
            fieldset.visible = computed(() =>
            {
                let visibleFields = 0
                for(const [name, field] of Object.entries(fieldset.fields as FieldsStructure))
                {
                    if(field.show !== undefined)
                    {
                        if(field.show(formFields) === true)
                        {
                            visibleFields++
                        }
                    }
                    else
                    {
                        visibleFields++
                    }
                }
                return visibleFields > 0
            })
        })
        // Handle validation:
        const validateForm = () =>
        {
            // Reset form data:
            Object.assign(formData, {})
            // Start reactive errors object:
            let validationErrors = reactive({} as ValidationErrorsStructure)
            // Reset error summary:
            store.commit('errors/resetFormErrors')
            // Loop fields in form:
            for(const [name, field] of Object.entries(formFields))
            {
                // Reset errors attached to field:
                delete formFields[name].errors
                // Loop validation rules for field:
                for(const [rule, config] of Object.entries(field.rules))
                {
                    validationErrors = validateInput(validationErrors, name, rule, config, formFields[name].value, formFields)
                }
                // Check if field has errors:
                if(hasErrors(validationErrors, name))
                {
                    // Add errors to field:
                    formFields[name].errors = validationErrors[name]
                }
                // Set into form data:
                formData[name] = formFields[name].value
            }
            // Check for any errors:
            if(hasErrors(validationErrors))
            {
                store.commit('errors/setFormErrors', formFields)
                router.push({ hash: '#error-summary-container' })
                return false
            }

            return true
        }
        // Handle input value updated:
        const handleInputValueUpdated = (data: InputValueUpdatedStructure) =>
        {
            const fieldName = data.name
            formFields[fieldName].value = data.value
            emit('formFieldValuesUpdated', formFields)
        }
        // Handle submit:
        const handleSubmitTriggered = (button: string) =>
        {
            formButtons[button].disabled = true
            // Handle input validation:
            const passedValidation = validateForm()
            // Check if we can proceed:
            if(passedValidation)
            {
                emit('formHasPassedValidation', formData)
            }
            formButtons[button].disabled = false
        }
        // Return anchor:
        const errorAnchor = (field: FieldStructure): string =>
        {
            return '#' + field.name + '-field-group'
        }
        // Get fieldset class string:
        const getFieldSetClassString = function(fieldset: FieldsetStructure): string
        {
            const sizeCLass = (fieldset.size !== undefined ? fieldset.size : 'govuk-fieldset__legend--l')
            return 'govuk-fieldset__legend ' + sizeCLass
        }

        // Return data:
        return {
            formFieldsets,
            formFields,
            formButtons,
            handleInputValueUpdated,
            handleSubmitTriggered,
            errorAnchor,
            getFieldSetClassString
        }
    }
})
